import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "sonner";

import { PageHeader } from "@/components/PageHeader";
import { UsageCard } from "@/components/UsageCard";
import { supabase } from "@/integrations/supabase/client";
import { useFounderAccess } from "@/hooks/useFounderAccess";
import { useSubscription, type Tier } from "@/hooks/useSubscription";
import { useSubscriptionActions } from "@/hooks/useSubscriptionActions";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { TIER_LABELS } from "@/lib/quotas";

export const Route = createFileRoute("/app/billing")({
  component: BillingPage,
  head: () => ({ meta: [{ title: "Billing — JobYoda" }, { name: "robots", content: "noindex" }] }),
});

type PlanRow = {
  productId: "starter_plan" | "pro_plan" | "premium_agentic_plan";
  tier: Tier;
  monthlyPriceId: string;
  yearlyPriceId: string;
  monthly: number;
};

const PLANS: PlanRow[] = [
  { productId: "starter_plan", tier: "starter", monthlyPriceId: "starter_monthly", yearlyPriceId: "starter_yearly", monthly: 9 },
  { productId: "pro_plan", tier: "pro", monthlyPriceId: "pro_monthly", yearlyPriceId: "pro_yearly", monthly: 19 },
  { productId: "premium_agentic_plan", tier: "premium_agentic", monthlyPriceId: "premium_agentic_monthly", yearlyPriceId: "premium_agentic_yearly", monthly: 69 },
];

type CatalogSetupResult = {
  ok?: boolean;
  error?: string;
  products?: Array<{
    productKey: string;
    status: "created" | "updated" | "exists";
    prices: Array<{
      lookupKey: string;
      status: "created" | "exists" | "needs_manual_fix";
      message?: string;
    }>;
  }>;
  issues?: Array<{ lookupKey: string; message?: string }>;
};

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function BillingPage() {
  const { subscription, tier, isActive, canceledAtPeriodEnd, loading: subLoading } = useSubscription();
  const { isFounder } = useFounderAccess();
  const actions = useSubscriptionActions();
  const { openCheckout, loading: checkoutLoading } = useStripeCheckout();
  const navigate = useNavigate();

  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogResult, setCatalogResult] = useState<CatalogSetupResult | null>(null);

  const currentProductId = subscription?.product_id as PlanRow["productId"] | undefined;
  const periodEnd = subscription?.current_period_end;

  const statusLabel = useMemo(() => {
    if (!subscription) return null;
    if (canceledAtPeriodEnd) return { label: "Cancels at period end", color: "warning" as const };
    if (subscription.status === "past_due")
      return { label: "Payment past due", color: "error" as const };
    if (subscription.status === "trialing")
      return { label: "Trial", color: "info" as const };
    if (isActive) return { label: "Active", color: "success" as const };
    return { label: subscription.status, color: "default" as const };
  }, [subscription, canceledAtPeriodEnd, isActive]);

  const handleOpenPortal = async () => {
    try {
      const { url } = await actions.openPortal();
      window.location.assign(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing portal.");
    }
  };

  const handleStartCheckout = async (plan: PlanRow) => {
    const priceId = cycle === "yearly" ? plan.yearlyPriceId : plan.monthlyPriceId;
    try {
      await openCheckout({ priceId });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open checkout.");
    }
  };

  const handleSetupCatalog = async () => {
    setCatalogLoading(true);
    setCatalogResult(null);
    try {
      const { data, error } = await supabase.functions.invoke<CatalogSetupResult>("stripe-catalog-setup", {
        body: {},
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCatalogResult(data ?? null);
      if (data?.ok) toast.success("Stripe catalog is ready for checkout.");
      else toast.warning("Stripe catalog needs one manual fix.");
    } catch (e) {
      const message = e instanceof Error ? e.message : "Could not set up Stripe catalog.";
      setCatalogResult({ ok: false, error: message });
      toast.error(message);
    } finally {
      setCatalogLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader title="Billing & subscription" subtitle="Manage your plan, payment method, and renewal via the Stripe customer portal." />

      {isFounder && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", alignItems: { sm: "center" }, gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Stripe catalog setup
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Creates/verifies the three JobYoda products and six lookup-key prices in your connected Stripe account.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={catalogLoading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                  onClick={handleSetupCatalog}
                  disabled={catalogLoading}
                >
                  {catalogLoading ? "Setting up…" : "Set up Stripe plans"}
                </Button>
              </Stack>

              {catalogResult?.error && <Alert severity="error">{catalogResult.error}</Alert>}
              {catalogResult && !catalogResult.error && (
                <Alert severity={catalogResult.ok ? "success" : "warning"}>
                  {catalogResult.ok
                    ? "Stripe products and prices are ready. Checkout can use starter_monthly, starter_yearly, pro_monthly, pro_yearly, premium_agentic_monthly, and premium_agentic_yearly."
                    : catalogResult.issues?.map((issue) => issue.message ?? issue.lookupKey).join(" ") || "One or more Stripe prices need a manual fix."}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Current plan card */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "space-between", alignItems: { sm: "center" }, gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Current plan</Typography>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mt: 0.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {TIER_LABELS[tier]}
                  </Typography>
                  {statusLabel && <Chip size="small" color={statusLabel.color} label={statusLabel.label} />}
                </Stack>
              </Box>
              {isActive && subscription && (
                <Stack spacing={0.5} sx={{ textAlign: { sm: "right" } }}>
                  <Typography variant="caption" color="text.secondary">
                    {canceledAtPeriodEnd ? "Access ends" : "Renews"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(periodEnd)}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {subscription?.status === "past_due" && (
              <Alert severity="error" action={
                <Button size="small" color="inherit" onClick={handleOpenPortal} disabled={actions.loading === "open_portal"}>
                  Fix payment
                </Button>
              }>
                Your last payment failed. Update your payment method in the Stripe portal.
              </Alert>
            )}
            {canceledAtPeriodEnd && (
              <Alert severity="warning" action={
                <Button size="small" color="inherit" onClick={handleOpenPortal} disabled={actions.loading === "open_portal"}>
                  Manage
                </Button>
              }>
                Your subscription will end on {formatDate(periodEnd)}. Resume from the Stripe portal before then.
              </Alert>
            )}

            {!subLoading && !isActive && (
              <Alert severity="info">
                You're on the Free plan. Upgrade below or visit{" "}
                <Link to="/pricing" style={{ fontWeight: 600 }}>pricing</Link>{" "}
                to compare plans.
              </Alert>
            )}

            {isActive && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={
                      actions.loading === "open_portal"
                        ? <CircularProgress size={14} color="inherit" />
                        : <SettingsIcon />
                    }
                    onClick={handleOpenPortal}
                    disabled={actions.loading === "open_portal"}
                  >
                    {actions.loading === "open_portal" ? "Opening…" : "Manage subscription"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CreditCardIcon />}
                    onClick={handleOpenPortal}
                    disabled={actions.loading === "open_portal"}
                  >
                    Update payment method
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Cancel, resume, change plans, or update payment — all handled in the Stripe customer portal.
                </Typography>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Choose-plan card (only shown when the user has no active subscription) */}
      {!isActive && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Choose a plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pick a plan to unlock higher monthly limits.
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={cycle}
                  exclusive
                  onChange={(_, v) => v && setCycle(v)}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                  <ToggleButton value="yearly">Yearly · save 20%</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                }}
              >
                {PLANS.map((plan) => {
                  const isCurrent = currentProductId === plan.productId && isActive;
                  const price = cycle === "yearly" ? Math.round(plan.monthly * 0.8 * 0.75) : Math.round(plan.monthly * 0.75);
                  return (
                    <Card key={plan.productId} variant="outlined" sx={{ borderColor: isCurrent ? "primary.main" : "divider", borderWidth: isCurrent ? 2 : 1 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {TIER_LABELS[plan.tier]}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            ${price}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {" "}/mo{cycle === "yearly" ? " · billed yearly" : ""}
                            </Typography>
                          </Typography>
                          <Button
                            variant="contained"
                            disabled={checkoutLoading}
                            onClick={() => handleStartCheckout(plan)}
                          >
                            Start with {TIER_LABELS[plan.tier]}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              <Box>
                <Button variant="text" onClick={() => navigate({ to: "/pricing" })}>
                  See full pricing & quota table →
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Usage */}
      <UsageCard />
    </Box>
  );
}
